<template>
  <div class="ma-4">
    <v-card height="90vh">
      <v-app-bar color="primary" class="d-sm-none" flat dense dark>
        <v-spacer></v-spacer>
        <v-app-bar-nav-icon @click.stop="drawer = !drawer" class="d-sm-none">
          <v-icon>mdi-filter</v-icon>
        </v-app-bar-nav-icon>
      </v-app-bar>

      <v-navigation-drawer
        v-model="drawer"
        left
        temporary
        absolute
        class="d-sm-none"
        flat
        width="300"
      >
        <CalendarSidebar
          :filters="filters"
          :upcoming-events="upcomingEvents"
          :has-active-filters="hasActiveFilters"
          @jump-to-today="handleJumpToToday"
          @reset-filters="handleResetFilters"
          @filter-changed="handleFilterChange"
          @get-option-count="handleGetOptionCount"
          @view-all-events="handleViewAllEvents"
        />
      </v-navigation-drawer>

      <div fluid class="fill-height pa-0">
        <v-snackbar
          v-model="snackbar.show"
          :color="snackbar.color"
          :timeout="3000"
        >
          {{ snackbar.text }}
          <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar.show = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>

        <div class="ma-4">
          <GoogleCalendarSync
          ref="googleCalendarSync"
          :initial-auth-state="isAuthorized"
          :current-date-range="currentDateRange"
          @auth-changed="handleAuthChanged"
          @sync-completed="handleSyncCompleted"
          @stats-updated="handleStatsUpdated"
          @show-success="showSuccess"
          @show-error="showError"
          />
        </div>

        <v-row no-gutters>
          <CalendarSidebar
            v-if="$vuetify.breakpoint.mdAndUp"
            :filters="filters"
            :upcoming-events="upcomingEvents"
            :has-active-filters="hasActiveFilters"
            @jump-to-today="handleJumpToToday"
            @reset-filters="handleResetFilters"
            @filter-changed="handleFilterChange"
            @get-option-count="handleGetOptionCount"
            @view-all-events="handleViewAllEvents"
          />

          <CalendarMain
            ref="calendar"
            :events="filteredEvents"
            :focus.sync="focus"
            :calendar-type="calendarType"
            :direct-care-staff="directCareStaff"
            :calendar-drawer.sync="calendarDrawer"
            @prev="handlePrev"
            @next="handleNext"
            @set-today="handleSetToday"
            @print-events="handlePrintEvents"
            @show-add="handleShowAdd"
            @calendar-change="handleCalendarChange"
            @event-click="handleEventClick"
            :upcoming-events="upcomingEvents"
            @view-all-events="handleViewAllEvents"
          />
        </v-row>
      </div>

      <div class="mt-8">
        <UpcomingEvents
          v-if="!$vuetify.breakpoint.mdAndUp"
          :upcoming-events="upcomingEvents"
          @view-all="$emit('view-all-events')"
        />
      </div>
    </v-card>

    <CalendarEventDialog
      v-model="eventDialog"
      :title-form="titleForm"
      :clients="clients"
      :programs="programs"
      :users="users"
      :event-data="selectedEvent"
      @save="handleSaveEvent"
      @close="eventDialog = false"
      @update-events="getEvents"
      @showSuccess="showSuccess"
      @showError="showError"
      @unsync-event="handleUnsyncSingleEvent"
    />
  </div>
</template>

<script>
/*eslint-disable */
import CalendarSidebar from "./Sidebar";
import CalendarMain from "./CalendarMain.vue";
import UpcomingEvents from "./Sidebar/UpcomingEvents.vue";
import CalendarEventDialog from "./CalendarEventDialog";
import GoogleCalendarSync from "@/components/Calendar/GoogleCalendarSync.vue";

import CalendarService from "../../service/calendar.service";
import moment from "moment";
import { datetime, RRule } from "rrule";

import axios from "axios";
const url = require("../../client_config/config").url;

import { EVENT_TYPES } from "@/constants/calendarConstants";

export default {
  name: "Calendar",

  components: {
    CalendarSidebar,
    CalendarMain,
    UpcomingEvents,
    CalendarEventDialog,
    GoogleCalendarSync,
  },

  data: () => ({
    eventForm: {
      name: "",
      program: null,
      startDate: "",
      endDate: "",
      notes: "",
      isRecurring: false,
      recurringType: "Daily",
    },
    isSaving: false,
    focus: new Date().toISOString().substr(0, 10),
    viewMode: 2,
    eventDialog: false,
    selectedEvent: null,
    calendarDrawer: false,
    directCareStaff: false,
    titleForm: "New Event",
    drawer: false,
    events: [],
    filters: [],
    calendarEventDate: {},
    isLoading: false,
    isAuthorized: false,
    userState: localStorage.getItem("userState"),
    snackbar: {
      show: false,
      text: "",
      color: "info",
    },
    clients: [],
    programs: [],
    users: [],
    eventTypes: EVENT_TYPES,
    syncStats: {
      totalEvents: 0,
      syncedEvents: 0,
      unsyncedEvents: 0,
      lastSyncDate: null
    },
  }),

  computed: {
    hasActiveFilters() {
      return this.filters.some((filter) =>
        filter.options.some((option) => option.selected)
      );
    },

    upcomingEvents() {
      const now = new Date();
      return this.events
        .filter((event) => new Date(event.start) >= now)
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .slice(0, 3);
    },

    filteredEvents() {
      if (!this.hasActiveFilters) return this.events;

      const activeFilters = this.filters
        .map((filter) => ({
          category: filter.title.toLowerCase(),
          selected: filter.options
            .filter((opt) => opt.selected)
            .map((opt) => opt.label),
        }))
        .filter((f) => f.selected.length > 0);

      return this.events.filter((event) => {
        return activeFilters.every((filter) => {
          switch (filter.category) {
            case "program":
              return filter.selected.includes(event.program?.Name || "");
            case "client":
              return filter.selected.includes(event.name || "");
            case "staff":
              return filter.selected.includes(event.staff?.Name || "");
            case "event type":
              return filter.selected.includes(event.event || "");
            default:
              return true;
          }
        });
      });
    },

    currentDateRange() {
      const focusDate = moment(this.focus);
      return {
        start: focusDate.clone().startOf('month').format('YYYY-MM-DD'),
        end: focusDate.clone().endOf('month').format('YYYY-MM-DD')
      };
    },

    calendarType() {
      return ["day", "week", "month"][this.viewMode];
    },
  },

  methods: {
    handleAuthChanged(isAuthorized) {
      this.isAuthorized = isAuthorized;
    },

    handleSyncCompleted() {
      this.getEvents(this.calendarEventDate);
    },

    handleStatsUpdated(stats) {
      this.syncStats = stats;
    },

    async handleUnsyncSingleEvent(eventId) {
      const success = await this.$refs.googleCalendarSync.unsyncSingleEvent(eventId);
      if (success) {
        await this.getEvents(this.calendarEventDate);
      }
    },

    showSuccess(text) {
      this.snackbar = {
        show: true,
        text,
        color: "success",
      };
    },

    showError(text) {
      this.snackbar = {
        show: true,
        text,
        color: "error",
      };
    },

    handleSaveEvent() {
      console.log("save");
    },

    getEvents(e) {
      if (!e || !e.start || !e.end) {
        console.warn("Event data is missing required properties.");
        return;
      }

      this.calendarEventDate = e;
      const startDate = moment(e.start.date).startOf("day").toISOString();
      const endDate = moment(e.end.date).endOf("day").toISOString();

      const params = {
        startDate,
        endDate,
        state: this.userState,
        limit: -1,
      };

      this.isLoading = true;
      CalendarService.get(params)
        .then((res) => {
          this.isLoading = false;
          let rawEvents = res.data.docs;
          let transformedEvents = [];

          rawEvents.forEach((calendarEvent) => {
            if (
              calendarEvent.isRecurringEvent &&
              calendarEvent.isRecurringEvent === "Yes"
            ) {
              transformedEvents = transformedEvents.concat(
                this.formatToRecurringCalendarEvent(calendarEvent)
              );
            } else {
              transformedEvents.push(this.mapCalendarEvent(calendarEvent));
            }
          });

          this.events = transformedEvents.filter(
            (event) => this.eventSelected === "Menu" || event.event !== "Menu"
          );
          
          if (this.isAuthorized) {
            this.$refs.googleCalendarSync?.refreshStats();
         }
       })
       .catch((err) => {
         console.error(err);
         this.isLoading = false;
         this.events = [];
       });
   },

   handleJumpToToday() {
     this.focus = new Date().toISOString().substr(0, 10);
   },

   handleResetFilters() {
     this.filters.forEach((filter) => {
       filter.options.forEach((option) => {
         option.selected = false;
       });
     });
   },

   handleFilterChange({ option, filterIndex }) {
     console.log(option, filterIndex);
     this.getEvents(this.calendarEventDate);
   },

   handleGetOptionCount(option) {
     return this.events.filter(
       (event) => event.name === option.label || event.details === option.label
     ).length;
   },

   handleViewAllEvents() {
     this.viewMode = 1;
   },

   handlePrev() {
     this.$refs.calendar.$refs.calendar.prev();
   },

   handleNext() {
     this.$refs.calendar.$refs.calendar.next();
   },

   handleSetToday() {
     this.focus = new Date().toISOString().substr(0, 10);
   },

   handlePrintEvents() {
     window.print();
   },

   handleShowAdd() {
     this.selectedEvent = null;
     this.titleForm = "New Event";
     this.eventDialog = true;
   },

   handleCalendarChange(e) {
     this.getEvents(e);
   },

   handleEventClick(e) {
     this.selectedEvent = e.event;
     this.titleForm = "Edit Event";
     this.eventDialog = true;
   },

   formatToRecurringCalendarEvent(calendarEvent) {
     if (!calendarEvent?.StartDate || !calendarEvent?.EndDate) {
       console.warn("Missing required date fields for recurring event");
       return [];
     }

     const StartDate = moment.utc(calendarEvent.StartDate);
     const EndDate = moment.utc(calendarEvent.EndDate);

     const shiftStartTime = StartDate.format("HH:mm");
     const shiftEndTime = EndDate.format("HH:mm");

     const recurringTypeSwitch = (eventType) =>
       ({
         Daily: RRule.DAILY,
         Weekly: RRule.WEEKLY,
        Monthly: RRule.MONTHLY,
        Annually: RRule.YEARLY,
      }[eventType] || RRule.DAILY);

    try {
      const rule = new RRule({
        freq: recurringTypeSwitch(calendarEvent.typeOfRecurringEvent),
        dtstart: datetime(
          StartDate.year(),
          StartDate.month() + 1,
          StartDate.date(),
          StartDate.hour(),
          StartDate.minute()
        ),
        until: datetime(
          EndDate.year(),
          EndDate.month() + 1,
          EndDate.date(),
          EndDate.hour(),
          EndDate.minute()
        ),
        interval: 1,
      });

      const recurringDates = rule.all();

      return recurringDates.map((date) => {
        const eventStart = moment.utc(date);
        let eventEnd = moment.utc(date);

        if (shiftEndTime < shiftStartTime) {
          eventEnd.add(1, "days");
        }

        const finalStartDate = eventStart.format(
          `YYYY-MM-DD ${shiftStartTime}`
        );
        const finalEndDate = eventEnd.format(`YYYY-MM-DD ${shiftEndTime}`);

        return {
          recurringEventId: calendarEvent._id,
          start: moment(finalStartDate).format("YYYY-MM-DD"),
          end: moment(finalEndDate).format("YYYY-MM-DD"),
          startTime: moment(finalStartDate).format("h:mma"),
          endTime: moment(finalEndDate).format("h:mma"),
          startDate: moment(finalStartDate).format("YYYY-MM-DD HH:mm"),
          endDate: moment(finalEndDate).format("YYYY-MM-DD HH:mm"),
          color: calendarEvent.Program?.ShiftColor || "#2196F3",
          event: calendarEvent.Event || "",
          name: calendarEvent?.Client?.Name || "",
          client: calendarEvent.Client || null,
          clientName: calendarEvent?.Client?.Name || "",
          staff: calendarEvent.Staff || null,
          program: calendarEvent.Program || null,
          note:
            calendarEvent.MedicalVisit?.Reason || calendarEvent.Notes || "",
          transportation: calendarEvent.Transportation || "No",
          Tfrom: calendarEvent.From || "",
          Tto: calendarEvent.To || "",
          createdBy: calendarEvent.CreatedBy || "",
          createdDate: calendarEvent.CreatedDate
            ? moment(calendarEvent.CreatedDate).format("YYYY-MM-DD")
            : "",
          signature: calendarEvent.Signature || null,
          visitID: calendarEvent.VisitID || null,
          eventFor: calendarEvent.EventFor || "",
          medicalVisit: calendarEvent.MedicalVisit || null,
          isRecurringEvent: calendarEvent.isRecurringEvent || "Yes",
          selectedOption: calendarEvent.typeOfRecurringEvent || "Monthly",
          googleCalendarSync: calendarEvent.googleCalendarSync || null,
          syncStatus: calendarEvent.googleCalendarSync?.synced ? 'synced' : 'unsynced'
        };
      });
    } catch (error) {
      console.error("Error generating recurring events:", error);
      return []; 
    }
  },

  mapCalendarEvent(calendarEvent) {
    if (!calendarEvent) {
      return {};
    }

    const { Program } = calendarEvent;
    return {
      id: calendarEvent._id,
      start: moment(calendarEvent.StartDate).format("YYYY-MM-DD"),
      end: moment(calendarEvent.EndDate).format("YYYY-MM-DD"),
      startTime: moment(calendarEvent.StartDate).format("h:mma"),
      startDate: moment(calendarEvent.StartDate).format("YYYY-MM-DD HH:mm"),
      endDate: moment(calendarEvent.EndDate).format("YYYY-MM-DD HH:mm"),
      endTime: moment(calendarEvent.EndDate).format("h:mma"),
      color: Program?.ShiftColor || this.colors,
      event: calendarEvent.Event,
      name: `${calendarEvent?.Client?.Name}`,
      client: calendarEvent.Client,
      clientName: calendarEvent?.Client?.Name,
      staff: calendarEvent.Staff,
      program: calendarEvent.Program,
      note: calendarEvent.MedicalVisit?.Reason || calendarEvent.Notes,
      transportation: calendarEvent.Transportation,
      Tfrom: calendarEvent.From,
      Tto: calendarEvent.To,
      createdBy: calendarEvent.CreatedBy,
      createdDate: moment(calendarEvent.CreatedDate).format("YYYY-MM-DD"),
      signature: calendarEvent.Signature,
      visitID: calendarEvent.VisitID,
      eventFor: calendarEvent.EventFor,
      medicalVisit: calendarEvent.MedicalVisit,
      isRecurringEvent: calendarEvent.isRecurringEvent,
      selectedOption: calendarEvent.typeOfRecurringEvent,
      googleCalendarSync: calendarEvent.googleCalendarSync || null,
      syncStatus: calendarEvent.googleCalendarSync?.synced ? 'synced' : 'unsynced'
    };
  },

  async getClients() {
    try {
      const userRole = localStorage.getItem("userRole");
      this.directCareStaff = userRole === "Direct Care Staff";

      const params = {};
      const companyIds = JSON.parse(
        localStorage.getItem("userCompanies") || "[]"
      );

      if (userRole !== "SuperAdministrator") {
        params["Company:in"] = companyIds;
        if (userRole !== "Administration") {
          params["State"] = this.userState;
        }
      }

      const response = await axios.get(`${url}/clients`, { params });
      this.clients = response.data.clients;

      const clientFilter = {
        title: "Client",
        icon: "mdi-account-outline",
        options: this.clients.map((client, index) => ({
          id: index + 1,
          label: client.Name,
          selected: false,
          color: "primary",
        })),
      };

      this.filters.push(clientFilter);
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw error;
    }
  },

  async getStaff() {
    try {
      const companyIds = JSON.parse(
        localStorage.getItem("userCompanies") || "[]"
      );
      const response = await axios.get(
        `${url}/users/state/${this.userState}`,
        {
          params: { Company: companyIds },
        }
      );
      this.users = response.data.users;

      const staffFilter = {
        title: "Staff",
        icon: "mdi-account-group-outline",
        options: this.users.map((user, index) => ({
          id: index + 1,
          label: user.Name,
          selected: false,
          color: "deep-purple",
        })),
      };

      this.filters.push(staffFilter);
    } catch (error) {
      console.error("Error fetching staff:", error);
      throw error;
    }
  },

  async getPrograms() {
    try {
      const companyIds = JSON.parse(
        localStorage.getItem("userCompanies") || "[]"
      );
      const response = await axios.get(
        `${url}/units/state/${this.userState}`,
        {
          params: { Company: companyIds },
        }
      );
      this.programs = response.data.units;

      const programFilter = {
        title: "Program",
        icon: "mdi-folder-outline",
        options: this.programs.map((program, index) => ({
          id: index + 1,
          label: program.Name,
          selected: false,
          color: "warning",
        })),
      };

      this.filters.push(programFilter);
    } catch (error) {
      console.error("Error fetching programs:", error);
      throw error;
    }
  },

  getEventType() {
    const eventFilter = {
      title: "Event Type",
      icon: "mdi-calendar-outline",
      options: this.eventTypes.map((event, index) => ({
        id: index + 1,
        label: event,
        selected: false,
        color: "primary",
      })),
    };
    this.filters.push(eventFilter);
  },
},

created() {
  this.getClients();
  this.getStaff();
  this.getPrograms();
  this.getEventType();
  this.getEvents({
    start: { date: moment().startOf("month").format("YYYY-MM-DD") },
    end: { date: moment().endOf("month").format("YYYY-MM-DD") },
  });
}
};
</script>

<style lang="scss" scoped>
.sync-status {
&--synced {
  color: #4caf50;
}

&--unsynced {
  color: #ff9800;
}

&--error {
  color: #f44336;
}
}
</style>